import { useMemo } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import { addDays, todayISO } from '@shared/dates';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useBlocksDueReview,
  useEvents,
  useMicrocycles,
  useObjectives,
  useReadiness,
  useSeasons,
  useSessions,
  useTeamLoad,
  useTeams,
} from '@/app/queries';
import { formatDate, sheetName } from '@/lib/format';
import { LoadProfile, type LoadDay } from '@/ui/LoadProfile';
import { LoadTrend } from '@/ui/LoadTrend';
import { weeksFrom } from '@/lib/loadTrend';
import { PageHeader, Panel, PanelLink, EmptyState, Notice } from '@/ui/Panel';
import { useStartProgress } from '@/app/progress';
import { START_STEPS } from '@/routes/guide/pages';
import { Sheet, SheetRow, SheetNumber } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';
import { ProvenanceChip } from '@/ui/Provenance';
import { Spinner } from '@/ui/Button';

/** Eight weeks is two mesocycles: long enough to show a ramp, short enough to read. */
const TREND_WEEKS = 8;

function monday(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return addDays(iso, -((d.getUTCDay() + 6) % 7));
}

/**
 * What is being worked on, and what needs a decision.
 *
 * Ordered the way a coach opens the app rather than the way the data is stored:
 * what is next, how this week is shaped, where the load has been going, who to
 * look at, and what is still open. Everything here is a link into the screen that
 * can act on it — a dashboard that only reports is a dashboard you stop opening.
 */
export function Dashboard({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const seasons = useSeasons();

  const today = todayISO();
  const teamId = (teams.data ?? [])[0]?.id;
  const weekStart = monday(today);
  const trendFrom = addDays(weekStart, -7 * (TREND_WEEKS - 1));

  const events = useEvents(teamId, today, addDays(today, 21));
  const weeks = useMicrocycles(teamId, weekStart, addDays(weekStart, 6));
  const sessionsThisWeek = useSessions(teamId, weekStart, addDays(weekStart, 6));
  const load = useTeamLoad(teamId, trendFrom, addDays(weekStart, 6));
  const readiness = useReadiness(teamId, today);
  const review = useBlocksDueReview();
  const objectives = useObjectives(teamId, 'open');

  // ── this week, as the load profile draws it ──────────────────────────
  const days: LoadDay[] = useMemo(() => {
    const week = weeks.data?.[0];
    const byDate = new Map((sessionsThisWeek.data ?? []).map((s) => [s.onDate, s]));
    const fixtures = new Set(
      (events.data ?? [])
        .filter((e) => e.kind === 'match' || e.kind === 'friendly')
        .map((e) => e.onDate),
    );
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(weekStart, i);
      const session = byDate.get(date);
      const planned = week?.days.find((d) => d.onDate === date);
      return {
        date,
        mdLabel: planned?.mdLabel ?? null,
        plannedLoad: session?.plannedLoad ?? planned?.plannedLoad ?? null,
        actualLoad: session?.actualLoad ?? null,
        isMatch: fixtures.has(date),
        isToday: date === today,
        label: session?.title ?? null,
      };
    });
  }, [weeks.data, sessionsThisWeek.data, events.data, weekStart, today]);

  const plannedThisWeek = days.reduce((n, d) => n + (d.plannedLoad ?? 0), 0);
  const actualThisWeek = days.reduce((n, d) => n + (d.actualLoad ?? 0), 0);

  // ── where the load has been going ────────────────────────────────────
  const trend = useMemo(() => {
    const starts = Array.from({ length: TREND_WEEKS }, (_, i) => addDays(trendFrom, i * 7));
    return weeksFrom(
      load.data?.sessions ?? [],
      load.data?.matches ?? [],
      starts,
      // The year is the same on every bar, so it is noise; the separator it
      // leaves behind ("Aug 24,") is noise that looks like a mistake.
      (s) => formatDate(s, locale).replace(/[\s,،.]*\d{4}\s*$/, '').trim(),
      today,
    );
  }, [load.data, trendFrom, locale, today]);

  // ── what is next ─────────────────────────────────────────────────────
  const next = useMemo(() => {
    const upcoming = (events.data ?? [])
      .filter((e) => e.onDate >= today)
      .sort((a, b) => a.onDate.localeCompare(b.onDate));
    const event = upcoming[0];
    if (!event) return null;
    const session = (sessionsThisWeek.data ?? []).find((s) => s.onDate === event.onDate);
    return { event, session, inDays: Math.round(
      (Date.parse(`${event.onDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
    ) };
  }, [events.data, sessionsThisWeek.data, today]);

  // ── who to look at ───────────────────────────────────────────────────
  // Readiness never gives a verdict, so this lists the players whose signals it
  // actually named and says nothing about the rest.
  const watch = (readiness.data ?? [])
    .filter((r) => !r.insufficientHistory && (r.status === 'watch' || r.status === 'fatigued' || r.status === 'high_concern'))
    .slice(0, 6);

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }
  if (teams.isError) return <Notice tone="risk">{t('error.generic')}</Notice>;

  const teamList = teams.data ?? [];
  const seasonList = seasons.data ?? [];
  const squadTotal = teamList.reduce((sum, tm) => sum + tm.playerCount, 0);
  const settingUp = squadTotal === 0 || seasonList.length === 0;

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('dashboard.title')}
        lead={t('dashboard.lead')}
        help={localePath(locale, '/guide/what')}
      />
        <Panel title={t('dashboard.noTeam')}>
          <EmptyState
            title={t('teams.empty')}
            body={t('dashboard.noTeamBody')}
            action={
              <Link
                to={localePath(locale, '/teams')}
                className="inline-flex min-h-11 items-center rounded-(--radius-control) bg-ink px-4 font-medium text-paper hover:bg-ink-muted"
              >
                {t('teams.create')}
              </Link>
            }
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        lead={t('dashboard.lead')}
        help={localePath(locale, '/guide/what')}
      />

      <StartHere locale={locale} />

      {/* Before there is a squad and a season there is nothing to chart, and a
          wall of empty panels under a checklist reads as a broken app rather
          than a new one. They come back the moment they can say something. */}
      {settingUp ? null : (
      <div className="grid gap-5">
        <Panel
          title={t('dashboard.next')}
          action={<PanelLink to={localePath(locale, '/calendar')}>{t('nav.calendar')}</PanelLink>}
        >
          {next ? (
            <div className="px-4 py-4">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-condensed text-headline font-bold">
                  {next.event.kind === 'match' || next.event.kind === 'friendly'
                    ? next.event.opponent || t('calendar.kind_match')
                    : next.session?.title || t(`calendar.kind_${next.event.kind}`)}
                </span>
                <Chip>
                  {next.inDays === 0
                    ? t('dashboard.today')
                    : t('dashboard.inDays', { count: next.inDays })}
                </Chip>
              </div>
              <p className="tabular mt-1 text-ink-muted">
                {formatDate(next.event.onDate, locale)}
                {next.event.startsAt ? ` · ${next.event.startsAt}` : ''}
                {next.event.location ? ` · ${next.event.location}` : ''}
              </p>
              {next.session?.objective ? (
                <p className="mt-3 max-w-[70ch]">{next.session.objective}</p>
              ) : (
                <p className="mt-3 text-meta text-ink-faint">{t('dashboard.noObjectiveYet')}</p>
              )}
              {/* Straight to the session, not to the list it is in. This is
                  the link a coach presses with a squad already waiting. */}
              <Link
                to={localePath(
                  locale,
                  next.event.kind === 'match' || next.event.kind === 'friendly'
                    ? '/matches'
                    : next.session
                      ? `/run/${next.session.id}`
                      : '/sessions',
                )}
                className="mt-1 inline-flex min-h-11 items-center text-meta underline decoration-line-strong underline-offset-2 hover:decoration-ink"
              >
                {next.event.kind === 'match' || next.event.kind === 'friendly'
                  ? t('dashboard.openMatch')
                  : t('dashboard.openSession')}
              </Link>
            </div>
          ) : (
            <p className="px-4 py-6 text-meta text-ink-muted">{t('dashboard.nothingNext')}</p>
          )}
        </Panel>

        <Panel
          title={t('dashboard.thisWeek')}
          count={
            plannedThisWeek || actualThisWeek
              ? t('dashboard.plannedActual', {
                  planned: Math.round(plannedThisWeek),
                  actual: Math.round(actualThisWeek),
                })
              : undefined
          }
          action={<PanelLink to={localePath(locale, '/plan')}>{t('nav.plan')}</PanelLink>}
          explain="session_load"
        >
          <LoadProfile days={days} />
        </Panel>

        <Panel
          title={t('dashboard.trend', { count: TREND_WEEKS })}
          action={<PanelLink to={localePath(locale, '/load')}>{t('nav.load')}</PanelLink>}
        >
          <LoadTrend weeks={trend} />
        </Panel>

        <div className="grid gap-5 lg:grid-cols-2">
          <Panel
            title={t('dashboard.watch')}
            count={watch.length || undefined}
            explain="readiness"
            action={<PanelLink to={localePath(locale, '/load')}>{t('nav.load')}</PanelLink>}
          >
            {watch.length === 0 ? (
              <p className="px-4 py-6 text-meta text-ink-muted">{t('dashboard.nobodyToWatch')}</p>
            ) : (
              <Sheet>
                {watch.map((r) => (
                  <SheetRow key={r.playerId}>
                    <SheetNumber>{r.shirtNumber ?? '–'}</SheetNumber>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {sheetName(r.firstName, r.lastName)}
                      </span>
                      <span className="text-meta text-ink-muted">
                        {/* `readiness` is not a namespace, and the signal is
                            already a `load.signal.*` key — so this printed
                            `readiness.signal_load.signal.ratioHigh` to anyone
                            whose squad ever had a signal. `Load.tsx` spells the
                            same lookup correctly. Third instance of this on
                            this screen. */}
                        {r.signals
                          .map((sig) => t(`load.signal.${sig.split('.').pop()}`))
                          .join(' · ')}
                      </span>
                    </span>
                    {/* The same figure, so the same statement about it. */}
                    <ProvenanceChip value={r.provenance} />
                    <Chip>{t(`load.status_${r.status}`)}</Chip>
                  </SheetRow>
                ))}
              </Sheet>
            )}
          </Panel>

          <Panel
            title={t('dashboard.open')}
            count={(review.data?.length ?? 0) + (objectives.data?.length ?? 0) || undefined}
          >
            {(review.data?.length ?? 0) === 0 && (objectives.data?.length ?? 0) === 0 ? (
              <p className="px-4 py-6 text-meta text-ink-muted">{t('dashboard.nothingOpen')}</p>
            ) : (
              <Sheet>
                {(review.data ?? []).map((b) => (
                  <SheetRow key={b.id} to={localePath(locale, '/blocks')}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{b.name}</span>
                      <span className="text-meta text-ink-muted">
                        {b.modifyIf || t('dashboard.blockDue')}
                      </span>
                    </span>
                    <Chip>{t('dashboard.review')}</Chip>
                  </SheetRow>
                ))}
                {(objectives.data ?? []).slice(0, 5).map((o) => (
                  <SheetRow key={o.id} to={localePath(locale, '/objectives')}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{o.title}</span>
                      <span className="text-meta text-ink-muted">
                        {t(`why.level_${o.level}`)}
                      </span>
                    </span>
                  </SheetRow>
                ))}
              </Sheet>
            )}
          </Panel>
        </div>
      </div>
      )}
    </>
  );
}

/**
 * The path through setting up, while there is still any of it left.
 *
 * The dashboard used to show two of these steps — add a squad, open a season —
 * and go quiet after them, which left a coach who had done both with no idea
 * that building a week and rating a session were what made the rest of the
 * product work. The Guide has had the full eight all along, on a page most
 * people never open.
 *
 * So it is the same eight steps and the same checks, read from
 * `useStartProgress`. It shows the next thing to do and what is already done,
 * and when the last one is ticked it disappears for good on its own — nothing
 * to dismiss, and nothing that can be dismissed by accident before it was read.
 */
function StartHere({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { state, done, total, next, complete, ready } = useStartProgress();
  const nextStep = START_STEPS.find((x) => x.key === next);

  // Nothing is drawn until the answers can be trusted. A checklist that says
  // "add your team" to a coach who has four is a checklist he stops believing.
  if (!ready || complete) return null;

  return (
    <Panel
      title={t('dashboard.nextSteps')}
      count={t('guide.progress', { done, total })}
      action={<PanelLink to={localePath(locale, '/guide')}>{t('dashboard.startHelp')}</PanelLink>}
    >
      {/*
        One action, said once.

        This was eight rows of equal weight, each carrying a green tick *and*
        the word "Done" — two statements of the same fact, seven times over —
        to communicate the single thing a coach should do next. A done step is
        now a quiet line; the next one is the only thing with any weight on it.
      */}
      <div className="border-b border-line bg-signal-soft px-4 py-4">
        <p className="text-meta text-ink-muted">{t('dashboard.doNext')}</p>
        <Link
          to={localePath(locale, nextStep ? nextStep.to : '/guide')}
          className="mt-0.5 inline-flex min-h-11 items-center font-condensed text-section font-semibold tracking-tight underline-offset-4 hover:underline"
        >
          {nextStep ? t(`guide.pages.start.steps.${nextStep.key}.title`) : t('guide.title')}
        </Link>
        {nextStep ? (
          <p className="max-w-[60ch] text-meta leading-relaxed text-ink-muted">
            {t(`guide.pages.start.steps.${nextStep.key}.body`)}
          </p>
        ) : null}
      </div>

      {/* What is already behind him, and what is still ahead — both quiet, and
          both one line. Progress stays legible without competing. */}
      <ol className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-3">
        {START_STEPS.map((step) => {
          const isDone = state[step.key];
          if (step.key === next) return null;
          return (
            <li
              key={step.key}
              className={`flex items-center gap-1.5 text-meta ${
                isDone ? 'text-ink-faint' : 'text-ink-muted'
              }`}
            >
              <span aria-hidden="true" className={isDone ? 'text-ok' : 'text-line-strong'}>
                {isDone ? '✓' : '○'}
              </span>
              <span className={isDone ? 'line-through decoration-line-strong' : ''}>
                {t(`guide.pages.start.steps.${step.key}.title`)}
              </span>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}
